import { Tabs, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { cn } from "@/modules/lib/utils"
import { BoxConsumableDetailsEntity } from "@modules/boxConsumable/entities/boxConsumableDetailsEntity"
import { BoxOfferInvitationStatus } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOfferInvitations } from "@modules/boxOffer/hooks/useBoxOffer"
import { useBoxPurchases } from "@modules/boxPurchases/hooks/useBoxPurchases"
import { BoxSubscriptionDetailsEntity } from "@modules/boxSubscription/entities/boxSubscriptionDetailsEntity"
import { word } from "@modules/core/utils/i18n"
import { MemberOffersList } from "@modules/members/memberOffers/components/memberOffersList"
import { startTransition, useMemo, useState } from "react"

type Props = {
  memberId: string
  userId: string
  setSubscriptionSelected?: React.Dispatch<React.SetStateAction<BoxSubscriptionDetailsEntity | null>>
  setIsOfferSupensionVisible?: React.Dispatch<React.SetStateAction<boolean>>
  setIsOfferDiscountVisible?: React.Dispatch<React.SetStateAction<boolean>>
  handleDeleteSubscription?: () => void
  handleShowCreditModal?: (consumableDetails: BoxConsumableDetailsEntity) => void
  className?: string
} & React.ComponentProps<"div">

export function MemberOffersWrapper({
  memberId,
  userId,
  setSubscriptionSelected,
  setIsOfferSupensionVisible,
  setIsOfferDiscountVisible,
  handleDeleteSubscription,
  handleShowCreditModal,
  className,
  ...props
}: Props) {
  const emptyText = {
    active: word("box.members.tab.offer.emptyText.noActive"),
    pending: word("box.members.tab.offer.emptyText.noPending"),
    expired: word("box.members.tab.offer.emptyText.noExpired"),
  }
  const { data: dataPurchases } = useBoxPurchases(memberId)
  const { data: dataInvitations } = useBoxOfferInvitations(userId)
  const [activeMembershipsIndex, setActiveMembershipsIndex] = useState("0")

  const activesPurchases = useMemo(() => dataPurchases.getActives(), [dataPurchases])
  const expiredsPurchases = useMemo(() => dataPurchases.getExpireds(), [dataPurchases])
  const pendingInvitations = useMemo(
    () => dataInvitations.filter((invitation) => invitation.status === BoxOfferInvitationStatus.PENDING),
    [dataInvitations],
  )
  const expiredInvitations = useMemo(
    () =>
      dataInvitations.filter((invitation) =>
        [BoxOfferInvitationStatus.REFUSED, BoxOfferInvitationStatus.CANCELLED].includes(invitation.status),
      ),
    [dataInvitations],
  )

  function selectTab(index: string) {
    startTransition(() => {
      setActiveMembershipsIndex(index)
    })
  }

  return (
    <div className={cn("flex w-full flex-col gap-4", className)} {...props}>
      <div className="flex w-full">
        <Tabs defaultValue={activeMembershipsIndex}>
          <TabsList variant="roundedButton">
            <TabsTrigger variant="roundedButton" onClick={() => selectTab("0")} value="0">
              {word("box.members.tab.offer.menu.activeMemberships")} (
              {activesPurchases.activeSubscriptions.length + activesPurchases.activeConsumables.length})
            </TabsTrigger>
            <TabsTrigger variant="roundedButton" onClick={() => selectTab("1")} value="1">
              {word("box.members.tab.offer.menu.pending")} ({pendingInvitations.length})
            </TabsTrigger>
            <TabsTrigger variant="roundedButton" onClick={() => selectTab("2")} value="2">
              {word("box.members.tab.offer.menu.expiredAndDeclined")} (
              {expiredsPurchases.expiredConsumables.length +
                expiredsPurchases.expiredSubscriptions.length +
                expiredInvitations.length}
              )
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="flex w-full">
        {activeMembershipsIndex === "0" && (
          <MemberOffersList
            memberId={memberId}
            boxConsumablesDetails={activesPurchases.activeConsumables}
            boxSubscriptionsDetails={activesPurchases.activeSubscriptions}
            emptyText={emptyText.active}
            setSubscriptionSelected={setSubscriptionSelected}
            setIsOfferSupensionVisible={setIsOfferSupensionVisible}
            setIsOfferDiscountVisible={setIsOfferDiscountVisible}
            handleDeleteSubscription={handleDeleteSubscription}
            handleShowCreditModal={handleShowCreditModal}
          />
        )}
        {activeMembershipsIndex === "1" && (
          <MemberOffersList
            memberId={memberId}
            boxOfferInvitations={pendingInvitations}
            emptyText={emptyText.pending}
            setSubscriptionSelected={setSubscriptionSelected}
            setIsOfferSupensionVisible={setIsOfferSupensionVisible}
            setIsOfferDiscountVisible={setIsOfferDiscountVisible}
            handleDeleteSubscription={handleDeleteSubscription}
            handleShowCreditModal={handleShowCreditModal}
          />
        )}
        {activeMembershipsIndex === "2" && (
          <MemberOffersList
            memberId={memberId}
            boxOfferInvitations={expiredInvitations}
            boxConsumablesDetails={expiredsPurchases.expiredConsumables}
            boxSubscriptionsDetails={expiredsPurchases.expiredSubscriptions}
            emptyText={emptyText.expired}
            setSubscriptionSelected={setSubscriptionSelected}
            setIsOfferSupensionVisible={setIsOfferSupensionVisible}
            setIsOfferDiscountVisible={setIsOfferDiscountVisible}
            handleDeleteSubscription={handleDeleteSubscription}
            handleShowCreditModal={handleShowCreditModal}
          />
        )}
      </div>
    </div>
  )
}
